import type { PropsWithChildren, ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { ToggleGroup, ToggleGroupItem } from 'ui/toggle-group'

// SKE-3789: re-skinned onto the kit's toggle-group primitive (ui/toggle-group).
// The declarative left/middle/right API is preserved for the Navbar.
// SKE-3806: items can carry an optional leading `icon`, and `appearance`
// selects the segmented-control treatment or the platform `nav` treatment
// (purple icon + black text selected / grey icon + grey text unselected).

export interface ToggleGroupItemConfig {
  value: string
  label: string
  /** Optional leading icon (e.g. a Font Awesome `<i>` or an svg). */
  icon?: ReactNode
}

export interface ToggleGroupProps {
  defaultValue: string
  leftItem: ToggleGroupItemConfig
  middleItem?: ToggleGroupItemConfig
  rightItem: ToggleGroupItemConfig
  onValueChange: (value: string) => void
  size: 'sm' | 'md' | 'lg'
  appearance?: 'segmented' | 'nav'
}

function ToggleGroupComponent({
  defaultValue,
  leftItem,
  middleItem,
  rightItem,
  onValueChange,
  size,
  appearance = 'segmented',
  ...props
}: PropsWithChildren<ToggleGroupProps & React.HTMLAttributes<HTMLDivElement>>) {
  const [value, setValue] = useState(defaultValue)

  useEffect(() => {
    setValue(defaultValue)
    onValueChange(defaultValue)
  }, [defaultValue])

  const items = [leftItem, middleItem, rightItem].filter(
    (item): item is ToggleGroupItemConfig => Boolean(item)
  )

  return (
    <div {...props}>
      <ToggleGroup
        type="single"
        value={value}
        // kit primitive offers sm/md; the old `lg` maps to md
        size={size === 'sm' ? 'sm' : 'md'}
        appearance={appearance}
        onValueChange={(next) => {
          if (next) {
            setValue(next)
            onValueChange(next)
          }
        }}
        orientation="horizontal"
      >
        {items.map((item) => (
          <ToggleGroupItem key={item.value} value={item.value}>
            {item.icon}
            {item.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  )
}

export default ToggleGroupComponent
